import React, { useState, useEffect, useRef } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import moment, { Moment } from "moment";
// import debug from "../debug";
import { cfd } from "./tools/cfd";
import { itemsToBoardState, getEvents } from "./tools/events";
import { Labels, Board } from "./types";

import "./CumulativeFlowDiagramView.css";

// const log = debug("CFD");

interface Props {
    board: Board;
    labels: Labels;
    columnId: string;
    from: Moment;
    to: Moment;
    loading: boolean;
}

const CumulativeFlowDiagramView: React.FC<Props> = function ({ board, labels, columnId, from, to, loading }: Props) {
    const axisColor = "var(--primary-font-color)";

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>({
        credits: {
            enabled: false
        },
        title: {
            text: undefined
        },
        chart: {
            animation: {
                duration: 100
            },
            height: null,
            backgroundColor: "var(--primary-background-color)"
        },
        xAxis: {
            type: "datetime",
            min: from.valueOf(),
            max: to.valueOf(),
            lineColor: axisColor,
            lineWidth: 2,
            tickColor: axisColor,
            labels: {
                style: {
                    color: axisColor,
                    fontFamily: "Roboto"
                }
            }
        },
        yAxis: {
            endOnTick: true,
            minTickInterval: 1,
            title: { text: "Item count", style: { color: axisColor } },
            labels: {
                style: {
                    color: axisColor,
                    fontFamily: "Roboto"
                }
            }
        },
        tooltip: {
            split: true
        },
        series: [],
        plotOptions: {
            area: {
                stacking: "normal",
                marker: {
                    enabled: false,
                    symbol: "circle",
                    states: {
                        hover: {
                            enabled: true
                        }
                    }
                }
            }
        },
        time: {
            useUTC: false
        },
        legend: {
            layout: "horizontal",
            align: "center",
            verticalAlign: "top",
            itemStyle: {
                color: "var(--primary-font-color)",
                cursor: "pointer",
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: "normal"
            },
            symbolRadius: 5,
            squareSymbol: true,
            margin: 40
        },
        loading: {
            labelStyle: {
                color: "var(--primary-font-color)",
                fontSize: "18px"
            },
            style: {
                backgroundColor: "var(--primary-background-color)"
            }
        }
    });

    useEffect(() => {
        if (board && columnId) {
            chartRef.current!.showLoading();
            const now = moment();
            const currentBoard = itemsToBoardState(board, now);
            const { startBoard, events } = getEvents(currentBoard, board.activity_logs, columnId, "status");
            const chartSeries = cfd(startBoard, events, columnId, labels, from, to);
            chartRef.current!.hideLoading();

            setChartOptions({
                xAxis: {
                    min: from.valueOf(),
                    max: to.valueOf()
                },
                series: chartSeries
            });
        }
    }, [board, columnId, labels, from, to]);

    useEffect(() => {
        if (loading) {
            chartRef.current!.showLoading();
        }
        return (): void => {
            chartRef.current!.hideLoading();
        };
    }, [loading]);

    const chartRef = useRef<Highcharts.Chart>();
    const highchartsCallback: Highcharts.ChartCallbackFunction = (chart) => {
        chartRef.current = chart;
    };

    return (
        <div className="cfd_view">
            <HighchartsReact
                highcharts={Highcharts}
                options={chartOptions}
                callback={highchartsCallback}
                containerProps={{ style: { height: "100%" } }}
            />
        </div>
    );
};

export default CumulativeFlowDiagramView;
